import React from 'react';
import {
    Box,
    Typography,
    Stack,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    ToggleButtonGroup,
    ToggleButton,
} from '@mui/material';
import { useInspectionStore } from '../Stores/InspectionStore';

interface GeneralInformationSectionProps {
    isMobile: boolean;
    customers: any[];
    locations: any[];
    onStorageChange: (storageId: string) => void;
}

const GeneralInformationSection: React.FC<GeneralInformationSectionProps> = ({
    isMobile,
    customers,
    locations,
    onStorageChange,
}) => {
    const formData = useInspectionStore((state) => state.formData);
    const updateField = useInspectionStore((state) => state.updateField);
    return (
        <Box sx={{ mb: isMobile ? 3 : 4 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>
                General Information
            </Typography>

            <Stack spacing={isMobile ? 3 : 2}>
                {/* Account, Storage, Date Row */}
                <Stack direction={isMobile ? "column" : "row"} spacing={2}>
                    <FormControl fullWidth size="small">
                        <InputLabel>Account</InputLabel>
                        <Select
                            value={formData.customer}
                            label="Account"
                            onChange={(e) => updateField('customer', e.target.value)}
                        >
                            {customers?.map((customer: any, idx: number) => (
                                <MenuItem key={idx} value={customer.customer}>
                                    {customer.customer}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl fullWidth size="small">
                        <InputLabel>Storage Location</InputLabel>
                        <Select
                            value={formData.storage}
                            label="Storage Location"
                            onChange={(e) => onStorageChange(e.target.value)}
                        >
                            {locations?.map((location: any) => (
                                <MenuItem key={location.id} value={location.id}>
                                    {location.locationName}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <TextField
                        fullWidth
                        size="small"
                        label="Date Serviced"
                        type="date"
                        value={formData.dateServiced ? 
                            (formData.dateServiced.includes('T') 
                                ? formData.dateServiced.split('T')[0] 
                                : formData.dateServiced) 
                            : ''}
                        onChange={(e) => updateField('dateServiced', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                </Stack>

                {/* Plenum, OSA Control, OSA Humidity Row */}
                <Stack direction={isMobile ? "column" : "row"} spacing={2}>
                    <TextField
                        fullWidth
                        size="small"
                        label="Plenum Set Point (°F)"
                        type="number"
                        value={formData.plenumSetPoint}
                        onChange={(e) => updateField('plenumSetPoint', e.target.value === '' ? '' : parseFloat(e.target.value))}
                        inputProps={{ step: 0.1 }}
                    />

                    <TextField
                        fullWidth
                        size="small"
                        label="OSA Control"
                        value={formData.osaControl}
                        onChange={(e) => updateField('osaControl', e.target.value)}
                    />

                    <TextField
                        fullWidth
                        size="small"
                        label="OSA Humidity (%)"
                        type="number"
                        value={formData.osaHumidity}
                        onChange={(e) => updateField('osaHumidity', e.target.value === '' ? '' : parseFloat(e.target.value))}
                        inputProps={{ min: 0, max: 100, step: 0.1 }}
                    />
                </Stack>

                {/* CO2, OSA Temp, System Mode Row */}
                <Stack direction={isMobile ? "column" : "row"} spacing={2}>
                    <TextField
                        fullWidth
                        size="small"
                        label="CO₂ (ppm)"
                        type="number"
                        value={formData.co2}
                        onChange={(e) => updateField('co2', e.target.value === '' ? '' : parseFloat(e.target.value))}
                        inputProps={{ step: 1 }}
                    />

                    <TextField
                        fullWidth
                        size="small"
                        label="OSA Temperature (°F)"
                        type="number"
                        value={formData.osaTemp}
                        onChange={(e) => updateField('osaTemp', e.target.value === '' ? '' : parseFloat(e.target.value))}
                        inputProps={{ step: 0.1 }}
                    />

                    <FormControl fullWidth size="small">
                        <InputLabel>System Mode</InputLabel>
                        <Select
                            value={formData.systemMode}
                            label="System Mode"
                            onChange={(e) => updateField('systemMode', e.target.value as any)}
                        >
                            <MenuItem value="Recirculation">Recirculation</MenuItem>
                            <MenuItem value="Cooling">Cooling</MenuItem>
                            <MenuItem value="Refrigeration">Refrigeration</MenuItem>
                            <MenuItem value="stand by">Stand By</MenuItem>
                            <MenuItem value="shut down">Shut Down</MenuItem>
                            <MenuItem value="normal">Normal</MenuItem>
                            <MenuItem value="run">Run</MenuItem>
                            <MenuItem value="forced">Forced</MenuItem>
                            <MenuItem value="refrigeration only">Refrigeration Only</MenuItem>
                        </Select>
                    </FormControl>
                </Stack>                

                {/* VFD Fan and CO2 Purge Row */}
                <Stack direction={isMobile ? "column" : "row"} spacing={2} alignItems={isMobile ? "stretch" : "center"}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1 }}>
                        <TextField
                            fullWidth
                            size="small"
                            label="VFD Fan"
                            type="number"
                            value={formData.vfdFan}
                            onChange={(e) => updateField('vfdFan', e.target.value === '' ? '' : parseFloat(e.target.value))}
                            inputProps={{ step: 0.1 }}
                        />

                        <ToggleButtonGroup
                            value={formData.vfdFanUnit}
                            exclusive
                            onChange={(e, newValue) => {
                                if (newValue !== null) {
                                    updateField('vfdFanUnit', newValue);
                                }
                            }}
                            size="small"
                        >
                            <ToggleButton value="%" sx={{ minWidth: 50 }}>
                                %
                            </ToggleButton>
                            <ToggleButton value="hz" sx={{ minWidth: 50 }}>
                                Hz
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Stack>

                    <TextField
                        fullWidth
                        size="small"
                        label="CO₂ Purge SP"
                        value={formData.co2PurgeSp}
                        onChange={(e) => updateField('co2PurgeSp', e.target.value)}
                        sx={{ flex: 1 }}
                    />
                </Stack>
            </Stack>
        </Box>
    );
};

export default GeneralInformationSection;
